import React, { FC, forwardRef } from 'react';
import { Editor } from '@tiptap/react';
import { PageNumberType } from '@kontentino/kontentino-constants/Pages';
import CharacterCounter, {
  CharacterCounterCountConfig,
} from 'app/modules/textEditor/components/optionsBar/CharacterCounter';
import {
  TextEditorFeature,
  TextEditorFeatures,
} from 'app/modules/textEditor/constants/textEditorFeature';
import OptionButton, {
  OptionButtonProps,
} from 'app/modules/textEditor/components/optionsBar/OptionButton';
import { DropzoneState } from 'react-dropzone';
import {
  Button,
  Icon,
  mergeTailwindClasses,
  Popover,
  Tooltip,
} from '@kontentino/ui';
import { faArrowRightToArc } from '@fortawesome/pro-regular-svg-icons/faArrowRightToArc';
import { faArrowRotateLeft } from '@fortawesome/pro-regular-svg-icons/faArrowRotateLeft';
import { faArrowRotateRight } from '@fortawesome/pro-regular-svg-icons/faArrowRotateRight';
import { faAt } from '@fortawesome/pro-regular-svg-icons/faAt';
import { faHashtag } from '@fortawesome/pro-regular-svg-icons/faHashtag';
import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink';
import { faPaperclip } from '@fortawesome/pro-regular-svg-icons/faPaperclip';
import { faSmile } from '@fortawesome/pro-regular-svg-icons/faSmile';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import clsx from 'clsx';
import EmojiPicker from 'app/components/EmojiPicker';
import DirectMention from 'components/shared/DirectMention';
import { DIRECT_MENTION_PAGE_TYPES } from 'app/modules/textEditor/constants/mentionNetworks';
import { useTranslation } from 'react-i18next';
import TextEditorAIContentPopup from 'app/modules/textEditor/components/optionsBar/TextEditorAIContentPopup';
import TextEditorConvertor from '../utils/textEditorConvertor';
import { PopoverContentProps } from '@radix-ui/react-popover';
import HashtagsPresets from 'app/modules/textEditor/components/optionsBar/HashtagsPresets';
import LinkShortener from 'app/modules/textEditor/components/optionsBar/LinkShortener';
import { faLanguage } from '@fortawesome/pro-regular-svg-icons/faLanguage';
import TextEditorTranslationPopup from 'app/modules/textEditor/components/optionsBar/TextEditorTranslationPopup';
import useBoolean from 'utils/hooks/useBoolean';

type Props = {
  editor: Editor;
  pageType?: PageNumberType;
  features: TextEditorFeatures;
  isDisabled?: boolean;
  dropzone: DropzoneState;
  optionsBarConfig?: OptionsBarConfig;
  dataNamePrefix?: string;
  charactersLimit?: number;
  className?: string;
  pageId: number | undefined;
};

export type OptionsBarConfig = {
  prependButtons?: OptionButtonProps[];
  additionalButtons?: OptionButtonProps[];
  charactersLimit?: number;
  count?: CharacterCounterCountConfig;
  popover?: {
    avoidCollisions?: boolean;
    initialOpen?: TextEditorFeature;
  };
};

const DEFAULT_POPOVER_CONTENT_PROPS = {
  className: '!tw-z-[1001] tw-min-h-[300px] tw-w-[480px]',
  avoidCollisions: true,
};

const OPTION_ICON_SIZE = 14;

const OptionsBar: FC<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      editor,
      pageType,
      features,
      isDisabled,
      optionsBarConfig,
      dropzone,
      dataNamePrefix,
      className,
      pageId,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [translationPopoverOpen, setTranslationPopoverOpen] = useBoolean(
      optionsBarConfig?.popover?.initialOpen === TextEditorFeature.Translation,
    );

    function getPrependButtons() {
      return (
        optionsBarConfig?.prependButtons?.map((props, index) => (
          <OptionButton
            {...props}
            dataName={
              props.dataName ? `${dataNamePrefix}-${props.dataName}` : undefined
            }
            key={`prepend-button-${index}`}
          />
        )) ?? []
      );
    }

    function getAdditionalButtons() {
      return (
        optionsBarConfig?.additionalButtons?.map((props, index) => (
          <OptionButton
            {...props}
            dataName={
              props.dataName ? `${dataNamePrefix}-${props.dataName}` : undefined
            }
            key={`additional-button-${index}`}
          />
        )) ?? []
      );
    }

    function getPopoverContentProps({
      className,
      avoidCollisions,
      ...rest
    }: PopoverContentProps = {}): PopoverContentProps {
      return {
        ...DEFAULT_POPOVER_CONTENT_PROPS,
        className: mergeTailwindClasses(
          clsx(DEFAULT_POPOVER_CONTENT_PROPS.className, className),
        ),
        avoidCollisions:
          optionsBarConfig?.popover?.avoidCollisions ??
          avoidCollisions ??
          DEFAULT_POPOVER_CONTENT_PROPS.avoidCollisions,
        ...rest,
      };
    }

    const hasPrependButtons =
      (optionsBarConfig?.prependButtons?.length ?? 0) > 0;
    const hasAIContent = features.includes(TextEditorFeature.AIContent);
    const hasLeftGroup = hasPrependButtons || hasAIContent;

    const hasRightGroup =
      features.includes(TextEditorFeature.UndoRedo) ||
      features.includes(TextEditorFeature.FileAttachments) ||
      features.includes(TextEditorFeature.EmojiPicker) ||
      features.includes(TextEditorFeature.HashtagsPresets) ||
      features.includes(TextEditorFeature.LinkShortener) ||
      features.includes(TextEditorFeature.DirectMention) ||
      features.includes(TextEditorFeature.Translation) ||
      (optionsBarConfig?.additionalButtons?.length ?? 0) > 0;

    return (
      <div
        className={mergeTailwindClasses(
          clsx(' tw-relative tw-flex tw-items-center tw-gap-2', className),
        )}
        id="editor-content-options-bar"
      >
        {hasLeftGroup && (
          <div className="tw-flex tw-items-center tw-gap-1">
            {getPrependButtons()}
            {features.includes(TextEditorFeature.AIContent) && (
              <Popover.Root
                defaultOpen={
                  optionsBarConfig?.popover?.initialOpen ===
                  TextEditorFeature.AIContent
                }
              >
                <Popover.Trigger asChild>
                  <OptionButton
                    isDisabled={isDisabled}
                    dataName={`${dataNamePrefix}-ai-content`}
                    className="tw-w-auto tw-border tw-border-dashed tw-border-grayscale-50 tw-px-2 tw-text-xs"
                  >
                    <Icon icon={faSparkles} />
                    <span>{t('useAI')}</span>
                  </OptionButton>
                </Popover.Trigger>
                <Popover.Portal>
                  <Popover.Content
                    {...getPopoverContentProps({
                      avoidCollisions: true,
                    })}
                    side="bottom"
                    align="start"
                    sideOffset={4}
                  >
                    <TextEditorAIContentPopup
                      pageType={pageType}
                      renderResultAction={(text) => (
                        <Tooltip content={t('insertToText')}>
                          <Popover.Close asChild>
                            <Button
                              onClick={() =>
                                editor.commands.insertContent(
                                  text.replace(/(\r\n|\n|\r)/gm, '<br>'),
                                  {
                                    parseOptions: {
                                      preserveWhitespace: true,
                                    },
                                  },
                                )
                              }
                              data-name={`ai-content_insert-text`}
                              variant="plain"
                              size="small"
                              className="tw-mr-3"
                            >
                              <Icon
                                icon={faArrowRightToArc}
                                className="tw-text-grayscale-100"
                              />
                              <span className="text-sm">{t('insert')}</span>
                            </Button>
                          </Popover.Close>
                        </Tooltip>
                      )}
                    />
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            )}
          </div>
        )}
        {hasLeftGroup && hasRightGroup && (
          <div className="tw-h-[20px] tw-w-px tw-bg-grayscale-50" />
        )}
        {features.includes(TextEditorFeature.UndoRedo) && (
          <>
            <OptionButton
              variant="plain"
              name={t('textEditor.undo')}
              isDisabled={isDisabled || !editor.can().undo?.()}
              onClick={() => editor.chain().focus().undo().run()}
              dataName={`${dataNamePrefix}-option-undo`}
            >
              <Icon icon={faArrowRotateLeft} size={OPTION_ICON_SIZE} />
            </OptionButton>
            <OptionButton
              variant="plain"
              name={t('textEditor.redo')}
              isDisabled={isDisabled || !editor.can().redo?.()}
              onClick={() => editor.chain().focus().redo().run()}
              dataName={`${dataNamePrefix}-option-redo`}
            >
              <Icon icon={faArrowRotateRight} size={OPTION_ICON_SIZE} />
            </OptionButton>
          </>
        )}
        {features.includes(TextEditorFeature.FileAttachments) && (
          <>
            <input {...dropzone.getInputProps()} />
            <OptionButton
              variant="plain"
              name={t('textEditor.attachments')}
              isDisabled={isDisabled}
              onClick={() => dropzone.open()}
              dataName={`${dataNamePrefix}-option-attach-file`}
            >
              <Icon icon={faPaperclip} size={OPTION_ICON_SIZE} />
            </OptionButton>
          </>
        )}
        {features.includes(TextEditorFeature.EmojiPicker) && (
          <Popover.Root
            defaultOpen={
              optionsBarConfig?.popover?.initialOpen ===
              TextEditorFeature.EmojiPicker
            }
          >
            <Popover.Trigger asChild>
              <OptionButton
                variant="plain"
                name={t('textEditor.emoji')}
                isDisabled={isDisabled}
                dataName={`${dataNamePrefix}-option-emoji`}
              >
                <Icon icon={faSmile} size={OPTION_ICON_SIZE} />
              </OptionButton>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                {...getPopoverContentProps({
                  avoidCollisions: true,
                  className: '!tw-w-auto',
                })}
              >
                <EmojiPicker
                  onEmojiSelect={(emoji) =>
                    editor.commands.insertContent(emoji.native)
                  }
                />
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        )}
        {pageType && features.includes(TextEditorFeature.HashtagsPresets) && (
          <Popover.Root
            defaultOpen={
              optionsBarConfig?.popover?.initialOpen ===
              TextEditorFeature.HashtagsPresets
            }
          >
            <Popover.Trigger asChild>
              <OptionButton
                variant="plain"
                name={t('textEditor.hashtagPresets')}
                isDisabled={isDisabled}
                dataName={`${dataNamePrefix}-hashtags-presets`}
              >
                <Icon icon={faHashtag} size={OPTION_ICON_SIZE} />
              </OptionButton>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content {...getPopoverContentProps()}>
                {pageId && (
                  <HashtagsPresets
                    pageId={pageId}
                    onCopyToText={(value) => {
                      editor
                        .chain()
                        .insertContent(TextEditorConvertor.textToHtml(value))
                        .setHardBreak()
                        .run();
                    }}
                    dataName="text-editor_hashtags-presets"
                    dataCy="text-editor_hashtags-presets"
                  />
                )}
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        )}
        {features.includes(TextEditorFeature.LinkShortener) && (
          <Popover.Root
            defaultOpen={
              optionsBarConfig?.popover?.initialOpen ===
              TextEditorFeature.LinkShortener
            }
          >
            <Popover.Trigger asChild>
              <OptionButton
                variant="plain"
                name={t('textEditor.linkShortener')}
                isDisabled={isDisabled}
                dataName={`${dataNamePrefix}-link-shortener`}
              >
                <Icon icon={faLink} size={OPTION_ICON_SIZE} />
              </OptionButton>
            </Popover.Trigger>

            <Popover.Portal>
              <Popover.Content {...getPopoverContentProps()}>
                <LinkShortener
                  pageId={pageId}
                  onCopyToText={editor.commands.insertContent}
                  dataName="text-editor_link-shortener"
                  dataCy="text-editor_link-shortener"
                />
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        )}
        {pageType &&
          pageId !== undefined &&
          DIRECT_MENTION_PAGE_TYPES.includes(pageType) &&
          [
            TextEditorFeature.DirectMention,
            TextEditorFeature.PageMention,
          ].every((s) => features.includes(s)) && (
            <Popover.Root
              defaultOpen={
                optionsBarConfig?.popover?.initialOpen ===
                TextEditorFeature.DirectMention
              }
            >
              <Popover.Trigger asChild>
                <OptionButton
                  variant="plain"
                  name={t('textEditor.directMention')}
                  isDisabled={isDisabled}
                  dataName={`${dataNamePrefix}-direct-mention`}
                >
                  <Icon icon={faAt} size={OPTION_ICON_SIZE} />
                </OptionButton>
              </Popover.Trigger>

              <Popover.Portal>
                <Popover.Content {...getPopoverContentProps()}>
                  <DirectMention
                    pageId={pageId}
                    pageType={pageType}
                    onCopyToText={editor.commands.insertContent}
                  />
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          )}
        {features.includes(TextEditorFeature.Translation) && editor && (
          <Popover.Root
            open={translationPopoverOpen}
            onOpenChange={setTranslationPopoverOpen.set}
            defaultOpen={
              optionsBarConfig?.popover?.initialOpen ===
              TextEditorFeature.Translation
            }
          >
            <Popover.Trigger asChild>
              <OptionButton
                variant="plain"
                name={t('textEditor.translation')}
                isDisabled={isDisabled}
                dataName={`${dataNamePrefix}-translation`}
              >
                <Icon icon={faLanguage} size={OPTION_ICON_SIZE} />
              </OptionButton>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                {...getPopoverContentProps({
                  avoidCollisions: true,
                })}
                side="bottom"
                align="start"
                sideOffset={4}
              >
                <TextEditorTranslationPopup
                  getTextToTranslate={() => {
                    const from = editor.state.selection.$from.pos;
                    const to = editor.state.selection.$to.pos;

                    return from === to
                      ? editor.getText()
                      : editor.state.doc.textBetween(from, to, ' ');
                  }}
                  onTranslated={(translation) => {
                    const htmlTranslation =
                      TextEditorConvertor.textToHtml(translation);

                    const from = editor.state.selection.$from.pos;
                    const to = editor.state.selection.$to.pos;

                    if (from === to) {
                      editor
                        .chain()
                        .setContent('')
                        .insertContent(htmlTranslation)
                        .run();
                    } else {
                      editor
                        .chain()
                        .insertContentAt({ from, to }, htmlTranslation)
                        .run();
                    }
                    setTimeout(() => {
                      editor.commands.focus();
                    }, 50);
                    setTranslationPopoverOpen.off();
                  }}
                />
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        )}
        {getAdditionalButtons()}
        {features.includes(TextEditorFeature.CharacterCounter) && (
          <CharacterCounter
            count={
              optionsBarConfig?.count || {
                characters: editor.storage.characterCount.characters(),
                hashtags: editor.storage.characterCount.specials().hashtags,
                mentions: editor.storage.characterCount.specials().mentions,
              }
            }
            limit={optionsBarConfig?.charactersLimit}
            pageType={pageType}
          />
        )}
      </div>
    );
  },
);

export default OptionsBar;
